import React, { useState } from 'react';
import { Grid, Typography, TextField, Button, Paper, CircularProgress } from '@mui/material';
import { useToastContext } from 'core/components/Toast/context/ToastContext';

export interface UpdatePasswordProps {
	onCancel?: () => void;
	onSubmit?: (newPassword: string) => void;
	isLoading?: boolean;
}

const UpdatePassword = (props: UpdatePasswordProps) => {
	const { onCancel, onSubmit, isLoading = false } = props;
	const { showToast } = useToastContext();
	const [newPassword, setNewPassword] = useState<string>('');
	const [confirmPassword, setConfirmPassword] = useState<string>('');
	const [newPasswordError, setNewPasswordError] = useState<string>('');
	const [confirmPasswordError, setConfirmPasswordError] = useState<string>('');
	const allowedSpecialChars = '!@#$%^&*()_+-=[]{}|;:,.<>?/~';
	const passwordRegex = new RegExp(
		'^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*()_+\\-=[\\]{}|;:,.<>?/~])[A-Za-z\\d!@#$%^&*()_+\\-=[\\]{}|;:,.<>?/~]{8,}$'
	);

	const validatePassword = (password: string): string => {
		if (!passwordRegex.test(password)) {
			return 'Password must contain at least 1 lowercase, 1 uppercase, 1 digit, and 1 allowed special character.';
		}
		// Check for disallowed special characters
		const specialChars = password.replace(/[A-Za-z0-9]/g, '');
		for (const char of specialChars) {
			if (!allowedSpecialChars.includes(char)) {
				return `Special character "${char}" is not allowed.`;
			}
		}
		return '';
	};

	const handlePasswordChange = (value: string) => {
		setNewPassword(value);
		// Required check
		if (!value || value.trim() === '') {
			setNewPasswordError('New password is required.');
			return;
		}
		const validation = validatePassword(value);
		setNewPasswordError(validation);
		// re-validate confirm
		if (confirmPassword && value !== confirmPassword) {
			setConfirmPasswordError('Passwords do not match.');
		} else if (!confirmPassword) {
			setConfirmPasswordError('');
		} else {
			setConfirmPasswordError('');
		}
	};

	const handleConfirmPasswordChange = (value: string) => {
		setConfirmPassword(value);
		if (!value || value.trim() === '') {
			setConfirmPasswordError('Confirm password is required.');
			return;
		}
		if (newPassword !== value) {
			setConfirmPasswordError('Passwords do not match.');
		} else {
			setConfirmPasswordError('');
		}
	};

	const handleSubmit = async (): Promise<void> => {
		// Required checks
		if (!newPassword || newPassword.trim() === '') {
			setNewPasswordError('New password is required.');
			return;
		}
		if (!confirmPassword || confirmPassword.trim() === '') {
			setConfirmPasswordError('Confirm password is required.');
			return;
		}

		// Validation
		const validation = validatePassword(newPassword);
		if (validation) {
			setNewPasswordError(validation);
			return;
		}

		// Match
		if (newPassword !== confirmPassword) {
			setConfirmPasswordError('Passwords do not match.');
			return;
		}

		setNewPasswordError('');
		setConfirmPasswordError('');

		// Call onSubmit - parent will handle success/error toast
		await onSubmit?.(newPassword);
	};

	return (
		<Grid container justifyContent="center">
			<Grid item xs={12} sx={{ height: '100%' }}>
				<Paper elevation={3} sx={{ p: 4, borderRadius: 2, height: '100%' }}>
					<Grid
						container
						justifyContent="space-between"
						alignItems="center"
						direction="row"
						sx={{ mb: 2 }}
					>
						<Grid item>
							<Button
								variant="contained"
								sx={{ backgroundColor: '#446388', marginTop: '3%', fontSize: '0.7rem' }}
								onClick={onCancel}
								disabled={isLoading}
							>
								Back
							</Button>
						</Grid>
						<Grid item>
							<Button
								variant="contained"
								sx={{ backgroundColor: '#446388', marginTop: '3%', fontSize: '0.7rem' }}
								onClick={handleSubmit}
								disabled={isLoading}
								startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
							>
								{isLoading ? 'Saving...' : 'Save'}
							</Button>
						</Grid>
					</Grid>
					<Grid container>
						<Grid>
							<Typography variant="h6" gutterBottom sx={{ fontFamily: 'Gotham' }}>
								Update Password
							</Typography>
						</Grid>
					</Grid>

					<Grid container spacing={2} mt={5}>
						<Grid item xs={12}>
							<TextField
								label="New Password"
								fullWidth
								type="Password"
								value={newPassword}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									handlePasswordChange(e.target.value)
								}
								error={Boolean(newPasswordError)}
								helperText={newPasswordError}
								sx={{ fontFamily: 'Gotham' }}
								disabled={isLoading}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Confirm New Password"
								fullWidth
								type="Password"
								value={confirmPassword}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									handleConfirmPasswordChange(e.target.value)
								}
								error={Boolean(confirmPasswordError)}
								helperText={confirmPasswordError}
								sx={{ fontFamily: 'Gotham' }}
								disabled={isLoading}
							/>
						</Grid>
					</Grid>
				</Paper>
			</Grid>
		</Grid>
	);
};

export default UpdatePassword;
